import { Box, List, ListItem, Stack, Typography } from "@mui/material";
import { ReactComponent as PrismAi } from "../../shared/assets/prism-ai/prismai-logo.svg";
import NavigateBackButton from "../../shared/components/customNavigateButton/customNavigateButton";

const PrivacyPolicy = () => {
  const policyList = [
    {
      header: "How We Use Your Information",
      subHeader: "We use your information to:",
      itemList: [
        "Provide and improve our procurement services",
        "Enable AI-based procurement optimization and suggestions",
        "Facilitate GST compliance and reporting",
        "Customize your experience on the platform",
        "Send alerts, updates, and communications",
        "Ensure security, fraud prevention, and regulatory compliance",
      ],
    },
    {
      header: "AI Enablement",
      subHeader: "Our platform uses AI models to offer features like:",
      itemList: [
        "Intelligent vendor suggestions",
        "Forecasting procurement needs",
        "Optimizing purchase cycles",
      ],
      note: "AI models may learn from anonymized, aggregated data to improve accuracy and user experience, but we do not use your personal or business data for training outside the platform’s scope.",
    },
    {
      header: "GST Integration",
      subHeader: "If you opt to integrate GST features, we may access:",
      itemList: [
        "GSTIN and registration details",
        "Invoice-level data for filing and compliance",
        "Tax computation, reconciliation, and validation information",
      ],
      note: "We handle GST data in compliance with applicable Indian tax regulations and do not share it with third parties except as required by law or with your explicit consent.",
    },
    {
      header: "Sharing Your Information",
      subHeader: "We do not sell your data. We may share your data with:",
      itemList: [
        "Service providers (e.g., hosting, cloud services, analytics)",
        "Government bodies if legally required (e.g., for GST filing or audit)",
        "Trusted partners under NDA for support or implementation purposes",
      ],
    },
    {
      header: "Data Security",
      subHeader: "We implement industry-standard security protocols:",
      itemList: [
        "Encryption at rest and in transit",
        "Role-based access controls",
        "Regular audits and vulnerability checks",
      ],
    },
    {
      header: "Your Rights",
      subHeader: "You have the right to:",
      itemList: [
        "Access and update your personal or business information",
        "Request deletion of your data (subject to regulatory constraints)",
        "Opt-out of marketing communications",
      ],
    },
    {
      header: "Cookies and Tracking",
      subHeader: "We use cookies and tracking tools to:",
      itemList: [
        "Enhance user experience",
        "Measure engagement and improve features",
        "Retain login sessions",
      ],
      note: "You can manage cookie preferences through your browser settings.",
    },
    {
      header: "Data Retention",
      subHeader: "We retain data only as long as necessary:",
      itemList: [
        "For the purposes outlined above",
        "To comply with legal and tax obligations (e.g., GST laws)",
      ],
    },
    {
      header: "Contact Us",
      subHeader:
        "For questions, concerns, or requests, contact us at: info@prismprocure.ai",
      itemList: [],
    },
  ];

  return (
    <Stack spacing={1} py={4} px={10}>
        <NavigateBackButton
        label={
                "Back to Home"
            }
            navigateLink={"/"}
        />
      <Stack
        direction={"row"}
        spacing={1}
        display={"flex"}
        justifyContent={"center"}
      >
        <PrismAi />
      </Stack>
      <Typography color={"#0F4761"} fontSize={36}>
        Privacy Policy
      </Typography>
      <Stack
        direction={"row"}
        display={"flex"}
        justifyContent={"space-between"}
      >
        <Typography fontSize={18} color={"#000000"}>
          Effective Date: 1-Jul-2023
        </Typography>
        <Typography fontSize={18} color={"#000000"}>
          Last Updated: 1-Aug-2025
        </Typography>
      </Stack>
      <Box>
        <Typography fontSize={18} color={"#000000"}>
          Welcome to PrismProcure.ai (“we,” “our,” or “us”). Your privacy is
          important to us, and we are committed to protecting the personal and
          business information you share with us when you use our platform.
        </Typography>
      </Box>
      <Box>
        <Typography fontSize={18} color={"#000000"}>
          This Privacy Policy explains how we collect, use, share, and protect
          your information when you visit or use PrismProcure.ai, including our
          AI-powered procurement tools and GST-integrated services.
        </Typography>
      </Box>
      <Stack>
            <Typography color={"#0F4761"} fontSize={24} fontWeight={600}>
              1. Information We Collect 
            </Typography>
            <Typography fontSize={18} color={"#000000"}>
            We may collect the following types of information: 
            </Typography>
            <Stack>
                <Typography color={"#0F4761"} fontSize={20} fontWeight={600}>
                a. Business Information 
                </Typography>
                <List sx={{ listStyleType: "disc", pl: 4 }}>
                    <ListItem sx={{ display: "list-item" }} >
                        <Typography fontSize={18} color={"#000000"}>
                        Company name, address, GSTIN 
                        </Typography>
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }} >
                        <Typography fontSize={18} color={"#000000"}>
                        Vendor and supplier details 
                        </Typography>
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }} >
                        <Typography fontSize={18} color={"#000000"}>
                        Procurement preferences, transaction history  
                        </Typography>
                    </ListItem>
                </List>
            </Stack>
            <Stack>
                <Typography color={"#0F4761"} fontSize={20} fontWeight={600}>
                b. Personal Information  
                </Typography>
                <List sx={{ listStyleType: "disc", pl: 4 }}>
                    <ListItem sx={{ display: "list-item" }} >
                        <Typography fontSize={18} color={"#000000"}>
                        Names, email addresses, and contact numbers of authorized users 
                        </Typography>
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }} >
                        <Typography fontSize={18} color={"#000000"}>
                        Role or designation within your organization  
                        </Typography>
                    </ListItem>
                </List>
            </Stack>
            <Stack>
                <Typography color={"#0F4761"} fontSize={20} fontWeight={600}>
                c. Financial & Tax Information 
                </Typography>
                <List sx={{ listStyleType: "disc", pl: 4 }}>
                    <ListItem sx={{ display: "list-item" }} >
                        <Typography fontSize={18} color={"#000000"}>
                        GST numbers and filings (for integration purposes)  
                        </Typography>
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }} >
                        <Typography fontSize={18} color={"#000000"}>
                        Invoice and billing details  
                        </Typography>
                    </ListItem>
                </List>
            </Stack>
            <Stack>
                <Typography color={"#0F4761"} fontSize={20} fontWeight={600}>
                d. Technical & Usage Information  
                </Typography>
                <List sx={{ listStyleType: "disc", pl: 4 }}>
                    <ListItem sx={{ display: "list-item" }} >
                        <Typography fontSize={18} color={"#000000"}>
                        IP address, browser type, device info 
                        </Typography>
                    </ListItem>
                    <ListItem sx={{ display: "list-item" }} >
                        <Typography fontSize={18} color={"#000000"}>
                        Pages visited, interactions, and AI tool usage data   
                        </Typography>
                    </ListItem>
                </List>
            </Stack>
          </Stack>
      {policyList.map((policy, index) => {
        return (
          <Stack key={index}>
            <Typography color={"#0F4761"} fontSize={24} fontWeight={600}>
              {index + 2}. {policy.header}
            </Typography>
            <Typography fontSize={18} color={"#000000"}>
              {policy.subHeader}
            </Typography>
            <List sx={{ listStyleType: "disc", pl: 4 }}>
              {policy.itemList.map((item, index) => {
                return (
                  <ListItem sx={{ display: "list-item" }} key={index}>
                    <Typography fontSize={18} color={"#000000"}>
                      {item}
                    </Typography>
                  </ListItem>
                );
              })}
            </List>
            {policy.note && (
              <Stack direction={"row"} spacing={1}>
                <Typography fontWeight={600} fontSize={18} color={"#000000"}>
                  Note:
                </Typography>
                <Typography fontSize={18} color={"#000000"}>
                  {policy.note}
                </Typography>
              </Stack>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
};

export default PrivacyPolicy;
